/**
 * Rota `/olts` — Fase 2 do TD26 roadmap.
 *
 * Grid de cards uma-por-OLT com contadores rápidos (online/bad/total) e
 * health bar. Click no card filtra o cockpit por aquela OLT.
 *
 * Source dos contadores: client-side aggregation via `useOltStats`, que
 * combina `useOltsQuery` (registry) com `useSnapshot` (todas as ONUs).
 * Cache compartilhado com /cockpit, então alternar entre rotas é grátis.
 */

import { OltCard } from '@/features/olts/OltCard';
import { useOltStats } from '@/features/olts/useOltStats';
import { useOltsQuery } from '@/features/olts/useOltsQuery';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/olts')({
  component: OltsPage,
});

function OltsPage() {
  const { data, isPending, isLoadingMore, isComplete, isError, loaded, total } = useOltStats();
  const oltsQ = useOltsQuery();

  if (isPending) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-sm text-fg-subtle">
        Carregando OLTs…
      </div>
    );
  }

  if (isError && data.length === 0) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-8 text-sm">
        <p className="text-status-critical">Falha ao carregar OLTs.</p>
        <p className="text-fg-subtle">
          Verifique conexão com backend. Reload da página tenta novamente.
        </p>
      </div>
    );
  }

  if (oltsQ.data && oltsQ.data.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8 text-sm text-fg-subtle">
        Nenhuma OLT cadastrada no registry. Verifique env <code>OLTS</code> do backend.
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto p-6">
      {/* Header da página */}
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-fg">OLTs</h1>
          <p className="mt-1 text-sm text-fg-subtle">
            {data.length} OLTs · {loaded.toLocaleString('pt-BR')}
            {!isComplete && total > 0 && ` / ${total.toLocaleString('pt-BR')}`} ONUs
            {!isComplete && isLoadingMore && (
              <span className="ml-2 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" aria-hidden />
                <span className="text-[11px]">carregando…</span>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Grid de cards. minmax(280px, 1fr) acomoda 1-4 colunas dependendo do
          width disponível (sidebar collapse afeta). */}
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
        {data.map((stat) => (
          <OltCard key={stat.olt_id} stat={stat} />
        ))}
      </div>
    </div>
  );
}
